import readXlsxFile from "read-excel-file"
import { ValidationError } from "sequelize"
import {
    CcLibraryItem,
    CcLibraryItemLoad,
    CcLibraryItemList,
    CcLibraryItemCreate,
    CcLibraryItemSave,
    CcLibraryItemSaveResult,
    CcLibraryHeadingList,
    CcLibraryItemsImport,
    CcLibraryNestedLoad,
} from "server/models"
import { MULTIPLE, NONE, VALUE, UNLIMITED } from "const"
import { AnswerType } from "client/utils"
import { GET, POST, DELETE, validate, search } from "client/store/utils"
import {
    LangType,
    PackageSettings,
    UserData,
} from "client/store"
import { TableGroupedIds, TableNestedIds } from "client/components"
import { CcLibraryItemValidation } from "./types"


export const LoadAllCcLibraryItems = (setId: number) => (
    GET(`/ccLibraryItem/set/${setId}`)
    .then(({ itemList, items }: CcLibraryItemLoad) => ({ itemList, items }))
)

export const LoadCcLibraryItemById = (itemId: number, setId: number) => (
    GET(`/ccLibraryItem/id/${itemId}/set/${setId}`)
    .then((item: CcLibraryItem) => item)
)

type SaveCcLibraryItemResponse = {
    newItemId: number
    newOrder: number
    validation: CcLibraryItemValidation
}
export const SaveCcLibraryItem = (
    id: number,
    item: CcLibraryItemCreate,
    maxItemNumber: number,
    lang: LangType,
) => (
    LoadAllCcLibraryItems(item.librarySetId).then(({ itemList }) => {
        // count custom library items
        let count = 0
        Object.values(itemList).forEach((row: CcLibraryItem) => {
            if (!item.projectId && row.accountId === item.accountId) count += 1
            if (item.projectId && row.projectId === item.projectId) count += 1
        })

        if (!id && maxItemNumber !== null && count >= maxItemNumber) {
            return <SaveCcLibraryItemResponse>{
                newItemId: null,
                newOrder: null,
                validation: <CcLibraryItemValidation>{
                    publicId: lang.maxLibraryItemsReached,
                },
            }
        }

        const data: CcLibraryItemSave = {
            id,
            item,
        }
        return POST(
            "/ccLibraryItem/save",
            JSON.stringify(data),
        ).then(({ response, order }: CcLibraryItemSaveResult) => {
            const newItem = response as CcLibraryItem
            const error = response as ValidationError

            return <SaveCcLibraryItemResponse>{
                newItemId: newItem.id || null,
                newOrder: order,
                validation: <CcLibraryItemValidation>validate(error, lang),
            }
        })
    })
)

export const UpdateManyCcLibraryItems = (data: CcLibraryItem[]) => (
    POST(
        "/ccLibraryItem/updateMany",
        JSON.stringify(data),
    )
)

export const DeleteCcLibraryItem = (itemId: number) => (
    DELETE(`/ccLibraryItem/delete/${itemId}`)
    .then((removedRows: number) => removedRows)
)

export const DeleteManyCcLibraryItems = (itemIds: number[]) => (
    POST(
        "/ccLibraryItem/deleteMany",
        JSON.stringify(itemIds),
    )
)

export const ImportCcLibraryItems = (data: CcLibraryItemsImport) => (
    POST(
        "/ccLibraryItem/import",
        JSON.stringify(data),
    )
)

export const LoadNestedItems = (
    setId: number,
    projectId?: number,
    accountId?: number,
) => (
    GET(`/ccLibraryItem/nested/set/${setId}`)
    .then(({ nested, headingList, headings, itemList, items }: CcLibraryNestedLoad) => {
        let count = 0
        if (projectId !== undefined && accountId !== undefined) {
            Object.values(itemList).forEach((item: CcLibraryItem) => {
                if (!projectId && item.accountId === accountId) count += 1
                if (projectId && item.projectId === projectId) count += 1
            })
        }

        return {
            count,
            nested,
            headingList,
            headings,
            itemList,
            items,
        }
    })
)

export const ImportCcLibraryItemsFromFile = (
    setId: number,
    projectId: number,
    userData: UserData,
    file: any,
    customItemNumber: number,
    maxNumber: number,
) => {
    let itemNumber = customItemNumber
    let stop = !(maxNumber === null || itemNumber < maxNumber)

    return readXlsxFile(file).then((rows: string[][]) => {
        const headingList: CcLibraryHeadingList = {}
        const itemList: CcLibraryItemList = {}

        const nestedHeadings: TableGroupedIds[] = [{
            groupId: null,
            nestedIds: [],
            nestedGroupedIds: [],
        }]

        let activeHeadings = nestedHeadings
        let activeItems = nestedHeadings[0].nestedIds as TableNestedIds[]
        let headingPrevLevel = 0
        let headingCurLevel = 0
        let itemPrevLevel = 0
        let itemCurLevel = 0

        const HEADING = "Heading"
        const COMPLIANT_ITEM = "Compliant Item"

        rows.every((rowUnconverted, rowId) => {
            if (stop) return false
            const row: string[] = []
            rowUnconverted.forEach((elem) => {
                row.push(elem ? elem.toString() : null)
            })

            const rowType = row[0] ? row[0].trim() : ""
            const headingTitle = row[1] ? row[1].trim() : ""
            const itemPublicId = row[2] ? row[2].trim() : ""
            const item = row[3] ? row[3].trim() : ""
            const itemOriginal = row[4] ? row[4].trim() : ""
            const itemInterpretation = row[5] ? row[5].trim() : ""
            const itemNote = row[6] ? row[6].trim() : ""

            let itemAnswerType = row[7] ? row[7].trim().toLowerCase() : ""
            if (itemAnswerType === "") itemAnswerType = "text"
            if (itemAnswerType === "yesno") itemAnswerType = "yes_no"

            const itemOptions = row[8] ? row[8].trim() : ""
            const itemGroup = row[9] ? row[9].trim() : ""

            if (rowType.indexOf(HEADING) >= 0) {
                headingCurLevel = parseInt(rowType.substr(HEADING.length), 10)
                if (headingCurLevel !== headingPrevLevel) {
                    activeHeadings = nestedHeadings
                    for (let i = 0; i < headingCurLevel; i++) {
                        if (activeHeadings.length > 0) {
                            activeHeadings = activeHeadings[activeHeadings.length - 1].nestedGroupedIds as TableGroupedIds[]
                        }
                    }
                    headingPrevLevel = headingCurLevel
                }

                headingList[rowId] = {
                    id: null,
                    headingId: null,
                    librarySetId: setId,
                    title: headingTitle,
                    order: activeHeadings.length,
                }

                const newNestedHeading: TableGroupedIds = {
                    groupId: rowId,
                    nestedIds: [],
                    nestedGroupedIds: [],
                }
                activeHeadings.push(newNestedHeading)
                activeItems = newNestedHeading.nestedIds as TableNestedIds[]
            }

            if (rowType.indexOf(COMPLIANT_ITEM) >= 0) {
                itemCurLevel = parseInt(rowType.substr(COMPLIANT_ITEM.length), 10)
                if (itemCurLevel !== itemPrevLevel) {
                    activeItems = activeHeadings[activeHeadings.length - 1].nestedIds as TableNestedIds[]
                    for (let i = 1; i < itemCurLevel; i++) {
                        if (activeItems.length > 0) {
                            activeItems = activeItems[activeItems.length - 1].nestedIds as TableNestedIds[]
                        }
                    }
                    itemPrevLevel = itemCurLevel
                }

                itemList[rowId] = {
                    id: null,
                    headingId: null,
                    itemId: null,
                    librarySetId: setId,
                    accountId: userData.accountId,
                    projectId,
                    publicId: itemPublicId,
                    compliantItem: item !== "" ? item : itemOriginal,
                    original: itemOriginal,
                    interpretation: itemInterpretation,
                    note: itemNote,
                    answerType: itemAnswerType as AnswerType,
                    options: itemAnswerType === MULTIPLE ? itemOptions : "",
                    group: itemGroup,
                    creator: userData.userId,
                    order: activeItems.length,
                }

                const newNestedItem: TableNestedIds = {
                    id: rowId,
                    nestedIds: [],
                }
                activeItems.push(newNestedItem)

                itemNumber += 1
                stop = !(maxNumber === null || itemNumber < maxNumber)
            }
            return true
        })

        return ImportCcLibraryItems({
            nested: nestedHeadings[0],
            headingList,
            itemList,
            newSetId: setId,
        })
    })
}

export const GetMaxCcLibraryItems = (
    packageSettings: PackageSettings,
    userData: UserData,
    projectId: number,
) => {
    const {
        ccCompliantItems,
        ccCompliantItemsValue,
        ccProjectCompliantItems,
        ccProjectCompliantItemsValue,
    } = packageSettings

    let maxItemNumber = 0
    if (!projectId) {
        if (ccCompliantItems === NONE) maxItemNumber = 0
        if (ccCompliantItems === VALUE && ccCompliantItemsValue) maxItemNumber = ccCompliantItemsValue
        if (ccCompliantItems === UNLIMITED) maxItemNumber = null
    } else {
        if (ccProjectCompliantItems === NONE) maxItemNumber = 0
        if (ccProjectCompliantItems === VALUE && ccProjectCompliantItemsValue) maxItemNumber = ccProjectCompliantItemsValue
        if (ccProjectCompliantItems === UNLIMITED) maxItemNumber = null
    }
    if (userData.accountId === 1) maxItemNumber = null

    return maxItemNumber
}

export const IsOwnerCcLibraryItem = (
    projectId: number,
    item: CcLibraryItem,
    userData: UserData,
) => (
    !projectId
        ? item && item.accountId === userData.accountId && item.creator === userData.userId
        : item && item.projectId === projectId && item.creator === userData.userId
)

export const SearchCcLibraryItems = (
    items: readonly CcLibraryItem[],
    searchString: string,
) => (
    items.filter((item) => search(searchString, item))
)
